import React, { useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Database, ShieldCheck, PlayCircle, Loader2, X } from 'lucide-react';

/**
 * Migrations panel for Settings.
 *
 * Currently exposes the book-closing metadata backfill migration:
 *   POST /api/admin/migrations/backfill-book-closing-metadata?dry_run=<bool>
 *
 * Dry-run is side-effect-free (writes nothing) and requires no re-auth.
 * Apply requires the admin's password, verified server-side against the
 * user's bcrypt hash (never client-side). The typed password is sent once
 * with the apply request, never stored in state longer than the request
 * itself, never logged, never rendered anywhere.
 */
const ResultPanel = ({ result }) => {
  if (!result) return null;
  const cs = result.financial_checksum || {};
  const totals = result.totals || {};
  return (
    <div className="cb-card" style={{ padding: 'var(--cb-space-5)', marginTop: 'var(--cb-space-4)' }} data-testid="migration-result-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="cb-eyebrow">Result</p>
          <h4 className="cb-section-title" style={{ marginTop: 4 }}>
            {result.mode === 'applied' ? 'Backfill applied' : 'Dry run — nothing was written'}
          </h4>
        </div>
        <span
          className={`cb-pill ${cs.match ? 'cb-pill--positive' : 'cb-pill--negative'}`}
          data-testid="migration-checksum-badge"
        >
          {cs.match ? '✓' : '✗'} financial checksum {cs.match ? 'matches' : 'DIVERGED'}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <MiniStat label="Clients in scope" value={result.scope_client_count} />
        <MiniStat label="Successful" value={result.successful_client_count} />
        <MiniStat label="Skipped" value={result.skipped_client_count} />
        <MiniStat label="Rows checked" value={cs.row_count_after || cs.row_count_before} />
      </div>

      {result.mode === 'applied' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MiniStat label="Client metadata writes" value={totals.client_metadata_writes} />
          <MiniStat label="Tagged INR_ERA" value={totals.payouts_tagged_inr_era} />
          <MiniStat label="Tagged USDT_ERA" value={totals.payouts_tagged_usdt_era} />
          <MiniStat label="Already matching" value={totals.payouts_book_already_matching} />
        </div>
      )}

      {result.clients && result.clients.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: 'var(--cb-space-4)' }}>
          <table className="cb-table" data-testid="migration-clients-table" style={{ minWidth: '100%' }}>
            <thead>
              <tr>
                <th>Client</th>
                <th>Conversion date</th>
                <th className="cb-num">Rate</th>
                <th className="cb-num">Payouts</th>
                <th className="cb-num">INR_ERA</th>
                <th className="cb-num">USDT_ERA</th>
                <th>Meta already set</th>
              </tr>
            </thead>
            <tbody>
              {result.clients.map((c) => (
                <tr key={c.client_id}>
                  <td style={{ fontWeight: 500 }}>{c.client_code}</td>
                  <td className="cb-muted cb-num" style={{ fontSize: '0.8125rem' }}>{c.conversion_date}</td>
                  <td className="cb-num">{c.conversion_rate}</td>
                  <td className="cb-num">{c.payouts_total}</td>
                  <td className="cb-num">{c.inr_era_count}</td>
                  <td className="cb-num">{c.usdt_era_count}</td>
                  <td>
                    {c.client_metadata_already_set ? (
                      <span className="cb-pill cb-pill--positive" style={{ fontSize: '0.6875rem' }}>yes</span>
                    ) : (
                      <span className="cb-pill" style={{ fontSize: '0.6875rem' }}>no</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result.skipped_clients && result.skipped_clients.length > 0 && (
        <div className="cb-card" style={{ padding: 'var(--cb-space-4)', borderColor: 'var(--cb-warning, #b47a00)' }}>
          <p className="cb-eyebrow" style={{ color: 'var(--cb-warning, #b47a00)' }}>Skipped</p>
          <ul style={{ margin: 0, paddingLeft: 'var(--cb-space-4)' }}>
            {result.skipped_clients.map((s) => (
              <li key={s.client_id} className="cb-muted" style={{ fontSize: '0.8125rem' }}>
                <strong>{s.client_code}</strong>: {s.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <details style={{ marginTop: 'var(--cb-space-4)' }}>
        <summary className="cb-muted" style={{ cursor: 'pointer', fontSize: '0.8125rem' }}>Show raw JSON</summary>
        <pre
          data-testid="migration-raw-json"
          style={{
            marginTop: 8, padding: 12,
            background: 'var(--cb-canvas-soft, #f6f5ef)',
            fontSize: '0.75rem', overflow: 'auto', maxHeight: 400,
            borderRadius: 4,
          }}
        >{JSON.stringify(result, null, 2)}</pre>
      </details>
    </div>
  );
};

const MiniStat = ({ label, value }) => (
  <div>
    <p className="cb-eyebrow" style={{ fontSize: '0.6875rem' }}>{label}</p>
    <p className="cb-num" style={{ fontSize: '1.125rem', fontWeight: 500 }}>{value ?? '—'}</p>
  </div>
);

const ApplyDialog = ({ open, onClose, onSubmit, submitting, scopeCount }) => {
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  if (!open) return null;
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    try {
      await onSubmit(password);
    } finally {
      // Wipe the password from state whether or not the submit succeeded.
      setPassword('');
    }
  };
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      data-testid="migration-apply-dialog"
      role="dialog"
      aria-modal="true"
    >
      <div className="cb-card" style={{ width: '100%', maxWidth: 480, padding: 'var(--cb-space-6)' }}>
        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="cb-eyebrow">Confirm apply</p>
            <h4 className="cb-section-title" style={{ marginTop: 4 }}>Apply book-closing backfill</h4>
          </div>
          <button
            type="button"
            className="cb-icon-btn"
            onClick={() => { setPassword(''); onClose(); }}
            aria-label="Close"
            data-testid="migration-apply-cancel-x"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="cb-body" style={{ marginBottom: 'var(--cb-space-4)' }}>
          This writes <strong>conversion metadata</strong> and <strong>era tags</strong> to
          {' '}<strong>{scopeCount}</strong> client{scopeCount === 1 ? '' : 's'} in scope. No financial
          value is modified. The operation is idempotent — re-running is safe.
        </p>

        <form onSubmit={handleSubmit}>
          <label className="cb-eyebrow" htmlFor="reauth-password" style={{ display: 'block', marginBottom: 4 }}>
            Re-enter admin password
          </label>
          <div style={{ position: 'relative', marginBottom: 'var(--cb-space-4)' }}>
            <input
              id="reauth-password"
              type={showPw ? 'text' : 'password'}
              className="cb-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              required
              disabled={submitting}
              data-testid="migration-apply-password-input"
              style={{ width: '100%', paddingRight: 60 }}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="cb-btn-ghost"
              style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', fontSize: '0.75rem' }}
              tabIndex={-1}
              data-testid="migration-apply-show-password"
            >
              {showPw ? 'Hide' : 'Show'}
            </button>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="cb-btn-secondary"
              onClick={() => { setPassword(''); onClose(); }}
              disabled={submitting}
              data-testid="migration-apply-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cb-btn-primary"
              disabled={submitting || !password}
              data-testid="migration-apply-confirm-btn"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Applying…</>) : 'Apply backfill'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MigrationsPanel = () => {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const previewDryRun = async () => {
    setRunning(true);
    try {
      const r = await axiosInstance.post(
        '/admin/migrations/backfill-book-closing-metadata?dry_run=true'
      );
      setResult(r.data);
      toast.success(`Dry run complete — ${r.data.scope_client_count} client(s) in scope`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dry run failed');
    } finally {
      setRunning(false);
    }
  };

  const applyBackfill = async (password) => {
    setApplying(true);
    try {
      const r = await axiosInstance.post(
        '/admin/migrations/backfill-book-closing-metadata?dry_run=false',
        { password },
      );
      setResult(r.data);
      setDialogOpen(false);
      toast.success(
        `Backfill applied — ${r.data.totals?.client_metadata_writes || 0} client meta writes, ` +
        `${(r.data.totals?.payouts_tagged_inr_era || 0) + (r.data.totals?.payouts_tagged_usdt_era || 0)} payout tag writes`
      );
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Apply failed';
      if (status === 423) {
        toast.error(`Locked out: ${detail}`);
        setDialogOpen(false);
      } else if (status === 401) {
        toast.error(detail);
        // keep dialog open so the admin can re-try (up to lockout threshold)
      } else {
        toast.error(detail);
        setDialogOpen(false);
      }
    } finally {
      setApplying(false);
    }
  };

  const scopeCount = result?.scope_client_count ?? 0;

  return (
    <div className="mb-6" data-testid="migrations-panel">
      <div className="cb-card" style={{ padding: 'var(--cb-space-6)' }}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="cb-eyebrow">Migrations</p>
            <h3 className="cb-section-title flex items-center gap-2" style={{ marginTop: 4 }}>
              <Database className="w-4 h-4" style={{ color: 'var(--cb-ink-soft)' }} />
              Book-closing metadata backfill
            </h3>
            <p className="cb-muted" style={{ marginTop: 'var(--cb-space-2)', fontSize: '0.8125rem', maxWidth: 720 }}>
              Writes <code>previous_base_currency</code>, <code>conversion_date</code>, and{' '}
              <code>conversion_rate</code> to already-converted clients, and tags each of their
              payouts with a <code>book</code> era (INR_ERA / USDT_ERA). Sourced from each
              client&apos;s own currency-conversion ledger pair — nothing hardcoded. Additive
              only: never touches <code>profit_amount</code>, <code>base_currency</code>, or
              any other financial value. Idempotent — re-running is safe.
            </p>
          </div>
          <ShieldCheck className="w-6 h-6" style={{ color: 'var(--cb-positive)' }} />
        </div>

        <div className="flex flex-wrap gap-2" style={{ marginTop: 'var(--cb-space-4)' }}>
          <button
            className="cb-btn-secondary"
            onClick={previewDryRun}
            disabled={running || applying}
            data-testid="migration-preview-button"
          >
            {running ? (<><Loader2 className="w-4 h-4 animate-spin" /> Running dry run…</>) : (<><PlayCircle className="w-4 h-4" /> Preview changes (dry run)</>)}
          </button>
          <button
            className="cb-btn-primary"
            onClick={() => setDialogOpen(true)}
            disabled={running || applying}
            data-testid="migration-apply-button"
          >
            {applying ? (<><Loader2 className="w-4 h-4 animate-spin" /> Applying…</>) : 'Apply backfill'}
          </button>
        </div>

        <ResultPanel result={result} />
      </div>

      <ApplyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={applyBackfill}
        submitting={applying}
        scopeCount={scopeCount}
      />
    </div>
  );
};

export default MigrationsPanel;
